import React, { useState } from 'react';
import { Sparkles, Image as ImageIcon, RefreshCw } from 'lucide-react';
import { generateBackgroundPrompt, generateBackgroundImage, uploadLessonBackground, clearPrompt } from '../../lib/api';

type Props = {
  coverUrl?: string;
  backgroundUrl?: string;
  lessonId?: number;
  lessonTitle?: string;
  learningObjectives?: string;
  courseId?: number;
  onBackgroundGenerated?: (imageUrl: string) => void;
};

export const BackgroundImageUploader: React.FC<Props> = ({ 
  coverUrl, 
  backgroundUrl: initialBackgroundUrl,
  lessonId,
  lessonTitle = '', 
  learningObjectives = '{}',
  courseId,
  onBackgroundGenerated 
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [promptResult, setPromptResult] = useState<string | null>(null);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [backgroundUrl, setBackgroundUrl] = useState<string | undefined>(initialBackgroundUrl);
  const [fromCache, setFromCache] = useState<boolean | null>(null);
  const [isClearing, setIsClearing] = useState(false);

  const generateImagePrompt = async () => {
    if (!lessonId) return;
    
    if (!coverUrl) {
      alert('错误：需要先设置封面图才能生成背景图提示词');
      return;
    }
    
    setIsGenerating(true);
    setPromptResult(null);
    setFromCache(null);
    
    try {
      let objectivesText = '';
      try {
        const objectives = JSON.parse(learningObjectives);
        objectivesText = Object.entries(objectives)
          .map(([key, value]) => `${key}: ${value}`)
          .join('\n');
      } catch (e) {
        objectivesText = learningObjectives;
      }
      
      console.log('Generating background prompt for lesson:', lessonId, lessonTitle);
      console.log('Learning objectives:', objectivesText);
      
      const response = await generateBackgroundPrompt(lessonId, lessonTitle, objectivesText, courseId);
      
      if (response.data.type === 'error') {
        const errorMessage = response.data.content || '生成提示词失败';
        alert(`错误：${errorMessage}`);
        setPromptResult('生成提示词失败，请重试。\n\n错误信息：' + errorMessage);
        return;
      }
      
      const result = response.data.prompt;
      setFromCache(response.data.from_cache);
      
      console.log('Received background prompt from AI:', result, '(from cache:', response.data.from_cache, ')');
      setPromptResult(result);
    } catch (error) {
      console.error('生成背景图提示词失败:', error);
      if (error.response && error.response.data && error.response.data.type === 'error') {
        const errorMessage = error.response.data.content || '生成提示词失败';
        alert(`错误：${errorMessage}`);
        setPromptResult('生成提示词失败，请重试。\n\n错误信息：' + errorMessage);
      } else {
        setPromptResult('生成提示词失败，请重试。\n\n错误信息：' + (error instanceof Error ? error.message : String(error)));
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClearPrompt = async () => {
    if (!lessonId) return;
    
    if (!confirm('确定要清空现有提示词并重新生成吗？')) {
      return;
    }
    
    setIsClearing(true);
    try {
      await clearPrompt(lessonId, 'background');
      setPromptResult(null);
      setFromCache(null);
      
      await generateImagePrompt();
    } catch (error) {
      console.error('清空提示词失败:', error);
      alert('清空提示词失败，请重试。');
    } finally {
      setIsClearing(false);
    }
  };

  const generateBackgroundImg = async () => {
    if (!promptResult || !lessonId) return;
    
    setIsGeneratingImage(true);
    
    try {
      console.log('Generating background image with prompt:', promptResult);
      const response = await generateBackgroundImage(lessonId, promptResult);
      
      if (response.data.type === 'error') {
        const errorMessage = response.data.content || '生成背景图片失败';
        alert(`错误：${errorMessage}`);
        return;
      }
      
      const imageUrl = response.data.image_url;
      
      console.log('Received background image URL:', imageUrl);
      
      console.log('Uploading background image URL to database for lesson:', lessonId);
      const uploadResponse = await uploadLessonBackground(lessonId, imageUrl);
      
      if (uploadResponse.data.type === 'error') {
        const errorMessage = uploadResponse.data.content || '保存背景图片失败';
        alert(`错误：${errorMessage}`);
        return;
      }
      
      console.log('Background image URL uploaded successfully');
      
      setBackgroundUrl(imageUrl);
      
      if (onBackgroundGenerated) {
        onBackgroundGenerated(imageUrl);
      }
      
      setPromptResult(null);
      
    } catch (error) {
      console.error('生成背景图片失败:', error);
      if (error.response && error.response.data && error.response.data.type === 'error') {
        const errorMessage = error.response.data.content || '生成背景图片失败';
        alert(`错误：${errorMessage}`);
      } else {
        alert('生成背景图片失败，请重试。\n\n错误信息：' + (error instanceof Error ? error.message : String(error)));
      }
    } finally {
      setIsGeneratingImage(false);
    }
  };

  return (
    <div className="p-8 flex flex-col items-center justify-center space-y-6">
      {!coverUrl && (
        <div className="w-full max-w-md p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-700 text-sm">
          提示：需要先在 Cover Image 选项卡中设置封面图，才能生成背景图。
        </div>
      )}
      
      {backgroundUrl ? (
        <div className="w-full max-w-md aspect-video bg-gray-100 rounded-lg overflow-hidden border shadow-sm">
          <img 
            src={backgroundUrl} 
            alt="Background" 
            className="w-full h-full object-contain" 
            onError={(e) => { (e.target as HTMLImageElement).src = "https://via.placeholder.com/800x450?text=Image+Load+Error"; }} 
          />
        </div>
      ) : (
        <div className="w-full max-w-md aspect-video bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center text-gray-400">
          <p>No background image generated yet.</p>
        </div>
      )}
      
      <button 
        onClick={generateImagePrompt}
        disabled={isGenerating || !coverUrl}
        className="flex items-center gap-2 bg-orange-100 text-orange-700 px-6 py-3 rounded-lg hover:bg-orange-200 transition-colors font-medium border border-orange-200 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Sparkles size={18} />
        {isGenerating ? '生成中...' : '获取背景图提示词'}
      </button>
      
      {promptResult && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">背景图提示词</h3>
              {fromCache !== null && (
                <span className={`text-xs px-2 py-1 rounded-full ${fromCache ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                  {fromCache ? '来自缓存' : '新生成'}
                </span>
              )}
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-gray-700 mb-4">
              {promptResult}
            </div>
            <div className="flex justify-end gap-3">
              <button 
                onClick={handleClearPrompt}
                disabled={isClearing}
                className="flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors font-medium border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw size={16} className={isClearing ? 'animate-spin' : ''} />
                {isClearing ? '清空中...' : '重新生成'}
              </button>
              <button 
                onClick={generateBackgroundImg}
                disabled={isGeneratingImage}
                className="flex items-center gap-2 bg-orange-600 text-white px-5 py-2 rounded-lg hover:bg-orange-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ImageIcon size={16} />
                {isGeneratingImage ? '生图中...' : '继续生图'}
              </button>
              <button 
                onClick={() => setPromptResult(null)}
                className="bg-gray-900 text-white px-5 py-2 rounded-lg hover:bg-gray-800 font-medium"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
